import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faChalkboardTeacher, faCommentDots, faClipboardCheck } from '@fortawesome/free-solid-svg-icons';
import Classes from './Classes';
import Pending from './Pending';
import Evaluation from './Evaluation';
import './MyClasses.css';

const API = process.env.REACT_APP_API_BASE_URL;

const TABS = [
  { key: 'classes', label: 'My Classes', icon: faChalkboardTeacher },
  { key: 'pending', label: 'Pending Feedback', icon: faCommentDots, count: 'pendingFeedbacks' },
  { key: 'evaluation', label: 'Evaluation', icon: faClipboardCheck, count: 'nbOfEvaluations' },
];

const DAYS = [
  ['yesterday', 'Yesterday'],
  ['today', 'Today'],
  ['tomorrow', 'Tomorrow'],
];

const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

// Same rule as before: evaluations that are NOT full pay need attention
const needsAttention = (item) => {
  if (item.calculatedFullpay !== undefined) return item.calculatedFullpay !== 0;
  const teacherMin = (item.TeacherDuration || 0) / 60;
  const studentMin = (item.StudentDuration || 0) / 60;
  return !(teacherMin >= 50 && studentMin >= 30);
};

const MyClasses = ({ userData }) => {
  // Tab is kept in the address (?tab=pending) so other pages can link straight to it
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const currentTab = TABS.some((t) => t.key === tabParam) ? tabParam : 'classes';

  const [selectedDay, setSelectedDay] = useState('today');
  const [counts, setCounts] = useState({ pendingFeedbacks: 0, nbOfEvaluations: 0 });

  const teacherId = userData?.id;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offsetMin = -new Date().getTimezoneOffset();
  const offsetLabel = `UTC${offsetMin >= 0 ? '+' : '-'}${String(Math.floor(Math.abs(offsetMin) / 60)).padStart(2, '0')}:${String(
    Math.abs(offsetMin) % 60
  ).padStart(2, '0')}`;

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    if (key === 'classes') next.delete('tab');
    else next.set('tab', key);
    setParams(next, { replace: true });
  };

  // ---------- Save the teacher's time zone (same logic) ----------
  useEffect(() => {
    if (!teacherId) return;
    (async () => {
      try {
        const offsetHours = -new Date().getTimezoneOffset() / 60;
        const res = await axios.get(`${API}/api/teacher/myclasses/OffsetList`, authHeader());
        let list = res.data;
        if (!Array.isArray(list) && list && typeof list === 'object') {
          list =
            (Array.isArray(list.data) && list.data) ||
            (Array.isArray(list.recordset) && list.recordset) ||
            (Array.isArray(list.result) && list.result) ||
            Object.values(list).filter((item) => item && typeof item === 'object' && item.Id !== undefined);
        }
        if (!Array.isArray(list)) list = [];

        let settingId = list.find((s) => s.Timezone === timeZone)?.Id ?? null;
        if (!settingId) {
          const added = await axios.post(`${API}/api/teacher/myclasses/addTimezone`, { timezone: timeZone, offsetHours }, authHeader());
          settingId = added.data.id;
        }
        if (settingId) {
          await axios.put(`${API}/api/teacher/myclasses/updateOffset/${teacherId}`, { offsetId: settingId }, authHeader());
        }
      } catch (error) {
        console.error('Error detecting/updating teacher timezone:', error);
      }
    })();
  }, [teacherId, timeZone]);

  // ---------- Badge counts (same endpoints), refreshed every minute ----------
  const fetchCounts = useCallback(async () => {
    if (!teacherId) return;
    let pendingFeedbacks = 0;
    let nbOfEvaluations = 0;
    try {
      const fb = await axios.get(`${API}/api/teacher/teacherfeedback/${teacherId}`, authHeader());
      pendingFeedbacks = fb.data?.pendingFeedbacks?.length || 0;
    } catch (error) {
      console.error('Error fetching pending feedback count:', error);
    }
    try {
      const ev = await axios.get(`${API}/api/teacher/teacherfeedback/evaluations/${teacherId}`, authHeader());
      nbOfEvaluations = (ev.data?.evaluations || []).filter(needsAttention).length;
    } catch (error) {
      console.error('Error fetching evaluations count:', error);
    }
    setCounts({ pendingFeedbacks, nbOfEvaluations });
  }, [teacherId]);

  useEffect(() => {
    if (!teacherId) return undefined;
    fetchCounts();
    const id = setInterval(fetchCounts, 60000);
    return () => clearInterval(id);
  }, [teacherId, fetchCounts]);

  if (!userData) {
    return (
      <div className="rmtc" aria-busy="true">
        <div className="rmtc-inner">
          <div className="rmtc-skel rmtc-skel--title" />
          <div className="rmtc-skel rmtc-skel--bar" />
          <div className="rmtc-skel rmtc-skel--body" />
        </div>
      </div>
    );
  }

  return (
    <div className="rmtc">
      <div className="rmtc-inner">
        <header className="rmtc-head">
          <div>
            <span className="rmtc-eyebrow">My Classes</span>
            <h1 className="rmtc-title">
              Your <span>teaching day</span>
            </h1>
          </div>
          <p className="rmtc-tz">
            <FontAwesomeIcon icon={faGlobe} /> {timeZone.replace(/_/g, ' ')} ({offsetLabel})
          </p>
        </header>

        <div className="rmtc-bar">
          <div className="rmtc-tabs" role="tablist" aria-label="My classes sections">
            {TABS.map((t) => {
              const n = t.count ? counts[t.count] : 0;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  id={`rmtc-tab-${t.key}`}
                  aria-selected={currentTab === t.key}
                  aria-controls="rmtc-panel"
                  className={`rmtc-tab ${currentTab === t.key ? 'is-on' : ''}`}
                  onClick={() => setTab(t.key)}
                >
                  <FontAwesomeIcon icon={t.icon} />
                  <span>{t.label}</span>
                  {n > 0 && (
                    <span className="rmtc-badge" aria-label={`${n} waiting`}>
                      {n}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {currentTab === 'classes' && (
            <div className="rmtc-days" role="radiogroup" aria-label="Day">
              {DAYS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={selectedDay === value}
                  className={selectedDay === value ? 'is-on' : ''}
                  onClick={() => setSelectedDay(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div id="rmtc-panel" role="tabpanel" aria-labelledby={`rmtc-tab-${currentTab}`} className="rmtc-panel">
          {currentTab === 'classes' && <Classes userData={userData} day={selectedDay} />}
          {currentTab === 'pending' && <Pending onSubmitrefresh={fetchCounts} userData={userData} />}
          {currentTab === 'evaluation' && <Evaluation userData={userData} />}
        </div>
      </div>
    </div>
  );
};

export default MyClasses;